import type { PrefetchContext, PrefetchedDataProps } from '#src/shared/prefetch';

import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';

import { prefetchTopicPageDataFromApi, useTopicPageDataFromApi } from './pageData';
import {
	prefetchTopicPagePaginatedPetitionUpdatesDataFromApi,
	useTopicPagePaginatedPetitionUpdatesDataFromApi,
} from './pagePetitionUpdatesPaginatedData';
import {
	prefetchTopicPagePaginatedRecentPetitionsDataFromApi,
	useTopicPagePaginatedRecentPetitionsDataFromApi,
} from './pageRecentPetitionsPaginatedData';
import { prefetchTopicSupporterVoicesDataFromApi, useTopicSupporterVoicesDataFromApi } from './pageSupporterVoiceData';
import {
	prefetchTopicPagePaginatedTrendingPetitionsDataFromApi,
	useTopicPagePaginatedTrendingPetitionsDataFromApi,
} from './pageTrendingPetitionsPaginatedData';
import {
	prefetchTopicPagePaginatedVictoriousPetitionsDataFromApi,
	useTopicPagePaginatedVictoriousPetitionsDataFromApi,
} from './pageVictoriousPetitionsPaginatedData';

export type TopicPagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof useTopicPageDataFromApi>[0]>;
	petitionUpdatesPaginatedData: NonNullable<Parameters<typeof useTopicPagePaginatedPetitionUpdatesDataFromApi>[0]>;
	recentPetitionsPaginatedData: NonNullable<Parameters<typeof useTopicPagePaginatedRecentPetitionsDataFromApi>[0]>;
	trendingPetitionsPaginatedData: NonNullable<Parameters<typeof useTopicPagePaginatedTrendingPetitionsDataFromApi>[0]>;
	victoriousPetitionsPaginatedData: NonNullable<
		Parameters<typeof useTopicPagePaginatedVictoriousPetitionsDataFromApi>[0]
	>;
	supporterVoicesData: NonNullable<Parameters<typeof useTopicSupporterVoicesDataFromApi>[0]>;
}>;

export type TopicPageCombinedState = MergedState<
	ReturnType<typeof useTopicPageDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedPetitionUpdatesDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedRecentPetitionsDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedTrendingPetitionsDataFromApi>,
	ReturnType<typeof useTopicPagePaginatedVictoriousPetitionsDataFromApi>,
	ReturnType<typeof useTopicSupporterVoicesDataFromApi>
>;

export function usePageContext(slug: string, prefetchedData?: TopicPagePrefetchedContext): TopicPageCombinedState {
	return useMergedStates(
		useTopicPageDataFromApi(prefetchedData?.pageData, slug),
		useTopicPagePaginatedPetitionUpdatesDataFromApi(prefetchedData?.petitionUpdatesPaginatedData, slug),
		useTopicPagePaginatedRecentPetitionsDataFromApi(prefetchedData?.recentPetitionsPaginatedData, slug),
		useTopicPagePaginatedTrendingPetitionsDataFromApi(prefetchedData?.trendingPetitionsPaginatedData, slug),
		useTopicPagePaginatedVictoriousPetitionsDataFromApi(prefetchedData?.victoriousPetitionsPaginatedData, slug),
		useTopicSupporterVoicesDataFromApi(prefetchedData?.supporterVoicesData, slug),
	);
}

export async function prefetchPageContext(slug: string, context: PrefetchContext): Promise<TopicPagePrefetchedContext> {
	const [
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		supporterVoicesData,
	] = await Promise.all([
		prefetchTopicPageDataFromApi(context, slug),
		prefetchTopicPagePaginatedPetitionUpdatesDataFromApi(context, slug),
		prefetchTopicPagePaginatedRecentPetitionsDataFromApi(context, slug),
		prefetchTopicPagePaginatedTrendingPetitionsDataFromApi(context, slug),
		prefetchTopicPagePaginatedVictoriousPetitionsDataFromApi(context, slug),
		prefetchTopicSupporterVoicesDataFromApi(context, slug),
	]);

	return {
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		supporterVoicesData,
	};
}

export type TopicPagePrefetchedDataProps = PrefetchedDataProps<TopicPagePrefetchedContext>;
